/**
 * The shell. The chart fills the screen; everything else floats over it and
 * clears away when done. This composes the map, the always-visible chrome, and
 * the sheets, so the resting screen is just chart, boat, four controls and one
 * number, and the rest is summoned when wanted.
 *
 * It surfaces the existing milestone work (M1 chart, M2 own ship, M3 chartwork,
 * M4 routing, M5 tide) through the agreed interface, plus the slice B fix
 * builder, which constructs a fix that preserves its observations.
 */
import { useEffect, useMemo, useState } from 'react';
import { MapView } from '@/map/MapView';
import { SimRunner } from '@/ownship/SimRunner';
import { BsbManifestLoader } from './BsbManifestLoader';
import { ChartPacksLoader } from './ChartPacksLoader';
import { UpdateCheckLoader } from './UpdateCheckLoader';
import { UpdatePrompt } from './UpdatePrompt';
import { RebuildControl } from './RebuildControl';
import { FeedbackSyncRunner } from '@/feedback/FeedbackSyncRunner';
import { NoaaManifestLoader } from './NoaaManifestLoader';
import { FesStationsLoader } from './FesStationsLoader';
import { AisSimRunner } from './AisSimRunner';
import { AisInternetRunner } from './AisInternetRunner';
import { AisSheet } from './AisSheet';
import { useShellStore } from '@/store/shell';
import { useChartPlotterStore } from '@/store';
import { useRoutingStore } from '@/store/routing';
import { selectActiveLeg } from '@/store/routing';
import { rhumbDistanceNm, formatDistance, formatBearing } from '@/nav';
import {
  monitorRoute,
  followLeg,
  clampLegIndex,
  clampFollowLegIndex,
  APPROACH_LEG_INDEX,
  legCount,
  steerSide,
  effectiveLegXtd,
  effectiveWaypointRadius,
} from '@/routing';
import { haptic } from '@/ui/haptics';
import { selectBuildingRoute } from '@/store/routing';
import { useSettingsStore } from '@/store/settings';
import { AreaSelectHint, AreaDownloadCard } from './AreaDownloadUI';
import { ChartEdgeRunner } from './ChartEdgeRunner';
import { routeTimedSummary, formatTtg } from './routeTiming';
import { useAlarmsStore } from '@/store/alarms';
import { useEnvStore } from '@/store/environment';
import { tidalStateAt, VICTORIA_WINDOW, VICTORIA_DEFAULT_MS, STREAM_SOURCES, ENAV_DERIVED_FROM } from '@/tides';
import { utcDayWindow } from '@/tides/curve';
import { ZOC_BANDS } from '@/chart';

import { Chrome } from './Chrome';
import { MobReadout } from './MobReadout';
import { MobSearchSheet } from './MobSearchSheet';
import { Backdrop, Toast } from './Sheet';
import { ModeSheet } from './ModeSheet';
import { DataFieldSheet } from './DataFieldSheet';
import { VesselSheet } from './VesselSheet';
import { ChartTapSheet, NoteSheet, WhatsHereSheet } from './ChartSheets';
import { FeatureSheet } from './FeatureSheet';
import { RouteSheet } from './RouteSheet';
import { PlotBanner } from './PlottingSheet';
import { PlanningSheet } from './PlanningSheet';
import { ConditionsSheet } from './ConditionsSheet';
import { WeatherTableSheet } from './WeatherTableSheet';
import { WeatherOverlayBar } from './WeatherOverlayBar';
import { TidesSheet } from './TidesSheet';
import { ForecastSheet } from './ForecastSheet';
import { FixBuilderSheet } from './FixBuilderSheet';
import { RunningFixSheet } from './RunningFixSheet';
import { DerivedSheet } from './DerivedSheet';
import { CtsSheet } from './CtsSheet';
import { TracksSheet } from './TracksSheet';
import { WaypointSheet } from './WaypointSheet';
import { RouteAlarmsSheet } from './RouteAlarmsSheet';
import { LegXtdSheet } from './LegXtdSheet';
import { AlarmsSheet } from './AlarmsSheet';
import { ClearingSheet } from './ClearingSheet';
import { PilotageSheet } from './PilotageSheet';
import { TransitSheet } from './TransitSheet';
import { HeadingSheet } from './HeadingSheet';
import { ClearingDepthSheet } from './ClearingDepthSheet';
import { DestinationSheet } from './DestinationSheet';
import { MenuSheet } from './MenuSheet';
import { DiamondSheet } from './DiamondSheet';
import { EblVrmSheet } from './EblVrmSheet';
import { SearchSheet } from './SearchSheet';
import { PilotagePlansSheet } from './PilotagePlansSheet';
import { PilotageCommunitySheet } from './PilotageCommunitySheet';
import { PilotagePlanToolbar } from './PilotagePlanToolbar';
import { PlanVisibilityChip, TideHeightChip, CommunityCountChip, EditPlanChip } from './PilotagePlanChips';
import { RadialRing } from './RadialRing';
import { TrackActionsPopover } from './TrackActionsPopover';
import { FirstBootOverlay } from './FirstBootOverlay';
import { Walkthrough } from './Walkthrough';
import { HapticsRunner } from './HapticsRunner';
import { AlarmSoundRunner } from './AlarmSoundRunner';
import { GnssAlarmsRunner } from './GnssAlarmsRunner';
import { BatteryAlarmRunner } from './BatteryAlarmRunner';
import { GlassRunner } from './GlassRunner';
import { NightRunner } from './NightRunner';
import { AutoPaletteRunner } from './AutoPaletteRunner';
import { AutoVariationRunner } from './AutoVariationRunner';
import { KeepAwakeRunner } from './KeepAwakeRunner';
import { MeasureReadout } from './MeasureReadout';
import { FeedbackButton } from '@/feedback/FeedbackButton';
import { AndroidBackRunner } from './AndroidBackRunner';
import { GnssHealthRunner } from '@/gnss/GnssHealthRunner';
import { HeartbeatRunner } from '@/diag/HeartbeatRunner';
import { AppStateRunner } from './AppStateRunner';
import { RouteFollowRunner } from './RouteFollowRunner';
import { GnssFixAgeChip } from './GnssFixAgeChip';
import { ArmedStrip } from './ArmedStrip';
import { TrainingBanner } from './TrainingBanner';
import { usePositionSourceStore } from '@/store/positionSource';
import { BackgroundServiceBanner } from './BackgroundServiceBanner';

/**
 * Caption for the illustrative tidal-stream overlay. The cells carry no real
 * tidal data, so the arrows are a single scenario value drawn across the view.
 * This says so plainly, with the current set and rate, so it's never mistaken
 * for charted/surveyed tidal information.
 */
function TideScrubber() {
  const planning = useShellStore((s) => s.mode === 'Plotting');
  const on = useChartPlotterStore((s) => s.groupVisibility['tidal-stream']);
  const toggleGroup = useChartPlotterStore((s) => s.toggleGroup);
  const ms = useEnvStore((s) => s.streamTimeMs);
  const setMs = useEnvStore((s) => s.setStreamTimeMs);
  const source = useEnvStore((s) => s.streamSource);
  const setSource = useEnvStore((s) => s.setStreamSource);
  const overlayMode = useEnvStore((s) => s.overlayMode);
  const scrubberOpen = useEnvStore((s) => s.scrubberOpen);
  const openScrubber = useEnvStore((s) => s.openScrubber);
  const closeScrubber = useEnvStore((s) => s.closeScrubber);
  // Streams overlay on: by default just draw the arrows at the current time with NO
  // bar in the way. A small chip summons the scrubber; closing it hides the bar and
  // snaps the time back to now. Weather barbs keep the always-on Plotting bar.
  const streamsVisible = !!on && overlayMode === 'streams';
  const weatherVisible = overlayMode === 'weather';
  const barVisible = (streamsVisible && scrubberOpen) || (planning && weatherVisible);
  const chipVisible = streamsVisible && !scrubberOpen;
  // Turn the overlay right off from the scrubber: stop the animation, hide the
  // arrows (toggle the group off), and collapse the bar. With the group off,
  // streamsVisible is false, so both the bar and the summon chip disappear.
  const turnStreamsOff = () => {
    setPlaying(false);
    if (on) toggleGroup('tidal-stream');
    closeScrubber();
  };
  // Play/animate: roll the shared clock forward so streams and the synthetic wind
  // barbs evolve on screen, the easiest way to watch a front come through. One hour
  // per tick; loops back to the window start when it runs off the end. Synthetic /
  // training clock only — the real models use wall-clock and their own slider.
  const [playing, setPlaying] = useState(false);
  // The source selector is collapsed behind a Source button to keep the bar tidy.
  const [showSrc, setShowSrc] = useState(false);
  useEffect(() => {
    if (!playing || !barVisible) return;
    const id = window.setInterval(() => {
      const cur = useEnvStore.getState().streamTimeMs;
      const next = cur + 3_600_000;
      useEnvStore.getState().setStreamTimeMs(next >= VICTORIA_WINDOW.endMs ? VICTORIA_WINDOW.startMs : next);
    }, 280);
    return () => window.clearInterval(id);
  }, [playing, barVisible]);
  if (!barVisible && !chipVisible) return null;
  // Date/time drives the streams; the SOURCE selector picks which dataset's
  // arrows render (one at a time) so a student can compare them on the same
  // moment. hours-from-HW and spring/neap come from the Victoria tide table.
  const st = tidalStateAt(ms);
  const d = new Date(ms);
  const dayWin = utcDayWindow(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  const wd = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getUTCDay()];
  const mon = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][
    d.getUTCMonth()
  ];
  const dateLabel = `${wd} ${d.getUTCDate()} ${mon} · ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} UT`;
  const h = st.hoursFromHW;
  const hwLabel =
    Math.abs(h) < 0.05 ? 'HW Victoria' : `HW Victoria ${h > 0 ? '+' : '−'}${Math.abs(h).toFixed(1)}h`;
  const sn = st.springRatio >= 0.85 ? 'springs' : st.springRatio <= 0.15 ? 'neaps' : 'springs–neaps';
  const HOUR = 3_600_000;
  const DAY = 86_400_000;
  const srcNote =
    source === 'enav'
      ? `RYA eNav · diamonds + 20 coverage points (~1 km approx) · springs/neaps from ${ENAV_DERIVED_FROM.join(' & ')}, whole year · not for navigation`
      : 'Chart diamonds · hourly, exact positions · RYA training data · not for navigation';
  // Arrows at NOW by default: a small chip stands in for the bar; tap it to scrub.
  const atNow = ms === VICTORIA_DEFAULT_MS;
  const chipLabel = atNow ? 'Now' : `HW ${h >= 0 ? '+' : '−'}${Math.abs(h).toFixed(1)}h`;
  if (!barVisible) {
    return (
      <button
        type="button"
        className="acp-mapchip"
        aria-label="Tidal streams time — tap to scrub through the hours"
        onClick={openScrubber}
      >
        <span className="ic" aria-hidden="true">🕑</span> Streams: {chipLabel}
      </button>
    );
  }
  return (
    <div className="acp-tide-scrubber">
      {streamsVisible && (
        <div className="schdr">
          <b>Tidal streams</b>
          <div className="hdbtns">
            <button
              type="button"
              className="streamsoff"
              aria-label="Turn the tidal-stream overlay off"
              onClick={turnStreamsOff}
            >
              Streams off
            </button>
            <button
              type="button"
              className="close"
              aria-label="Collapse the time controls (streams stay on)"
              title="Collapse — streams stay on"
              onClick={() => {
                setPlaying(false);
                closeScrubber();
              }}
            >
              ✕
            </button>
          </div>
        </div>
      )}
      {streamsVisible && (
        <div className="srcwrap">
          <button
            type="button"
            className="srcbtn"
            aria-expanded={showSrc}
            onClick={() => setShowSrc((v) => !v)}
          >
            Source: {STREAM_SOURCES.find((s2) => s2.id === source)?.name ?? source} {showSrc ? '▴' : '▾'}
          </button>
          {showSrc && (
            <div className="srcsel">
              {STREAM_SOURCES.map((s2) => (
                <button
                  key={s2.id}
                  type="button"
                  className={source === s2.id ? 'on' : ''}
                  onClick={() => {
                    setSource(s2.id);
                    setShowSrc(false);
                  }}
                >
                  <b>{s2.name}</b>
                  <span>{s2.character}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      <div className="hd">{dateLabel}</div>
      <div className="sub2">
        {hwLabel} · {sn}
      </div>
      {/* Date controls: same day-scoped model as the Heights tab — step a whole
          day, jump to any date with the calendar, or snap back to now. */}
      <div className="daterow">
        <button type="button" className="nav" aria-label="Previous day" onClick={() => setMs(ms - DAY)}>
          ‹
        </button>
        <label className="cal" title="Pick a date" aria-label="Pick a date">
          📅
          <input
            type="date"
            value={`${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`}
            onChange={(e) => {
              const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(e.target.value);
              if (!m) return;
              const tod = ms - dayWin.start; // keep the time of day
              setMs(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 0, 0, 0) + tod);
            }}
          />
        </label>
        <button type="button" className="nav" aria-label="Next day" onClick={() => setMs(ms + DAY)}>
          ›
        </button>
        <button
          type="button"
          className="now"
          aria-label="Go to now"
          onClick={() => {
            setPlaying(false);
            setMs(VICTORIA_DEFAULT_MS);
          }}
        >
          Now
        </button>
      </div>
      {/* The scrub bar spans just this one day's times, not the whole year. */}
      <div className="scrub">
        <button type="button" aria-label="Back one hour" onClick={() => setMs(ms - HOUR)}>
          −1h
        </button>
        <input
          type="range"
          min={dayWin.start}
          max={dayWin.end}
          step={1_800_000}
          value={ms}
          aria-label="Time of day"
          onChange={(e) => setMs(Number(e.target.value))}
        />
        <button type="button" aria-label="Forward one hour" onClick={() => setMs(ms + HOUR)}>
          +1h
        </button>
      </div>
      <div className="seg">
        <button
          type="button"
          className={playing ? 'on' : ''}
          aria-pressed={playing}
          aria-label={playing ? 'Pause the animation' : 'Play through the tide'}
          onClick={() => setPlaying((p) => !p)}
        >
          {playing ? '⏸ Pause' : '▶ Play'}
        </button>
      </div>
      <div className="ft">{weatherVisible ? 'Wind barbs · synthetic training weather · tracks this time · not a real forecast' : srcNote}</div>
    </div>
  );
}

/** When the CATZOC (data-quality) overlay is on, a chip that opens the colour key. */
function CatzocLegendChip() {
  const on = useChartPlotterStore((s) => s.groupVisibility['catzoc']);
  const [open, setOpen] = useState(false);
  if (!on) return null;
  if (!open) {
    return (
      <button
        type="button"
        className="acp-mapchip"
        aria-label="Show the data-quality (CATZOC) colour key"
        onClick={() => setOpen(true)}
      >
        <span className="ic" aria-hidden="true">▦</span> CATZOC
      </button>
    );
  }
  return (
    <div className="acp-catzoc-panel">
      <div className="schdr">
        <b>Data quality (CATZOC)</b>
        <button
          type="button"
          className="close"
          aria-label="Close the legend"
          onClick={() => setOpen(false)}
        >
          ✕
        </button>
      </div>
      <span className="lead">
        Zones of Confidence rate how accurate the survey behind the chart is; the figure is the
        horizontal accuracy each band implies.
      </span>
      <div className="acp-zoc-legend">
        {ZOC_BANDS.map((b) => (
          <div key={b.band} className="row">
            <span className="sw" style={{ background: b.colour }} />
            <span className="txt">
              <b>{b.band}</b> {b.accuracy} · {b.meaning}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Shown while the fix builder is waiting for a mark to be tapped. */
function FixArmHint() {
  const fixArmed = useShellStore((s) => s.fixArmed);
  if (!fixArmed) return null;
  return <div className="acp-hint">Tap a mark on the chart for this observation.</div>;
}

/**
 * Shown while place-anchor mode is waiting for a tap. Says "where the anchor
 * went in" rather than "where the anchor is", because on a boat lying to its
 * rode those are two different places and the one we want is the far end of the
 * chain.
 */
function AnchorArmHint() {
  const armed = useShellStore((s) => s.anchorPickArmed);
  const cancel = useShellStore((s) => s.cancelAnchorPick);
  if (!armed) return null;
  return (
    <div className="acp-hint">
      Tap the chart where the anchor went in.
      <button
        type="button"
        className="acp-hint-cancel"
        onClick={cancel}
        aria-label="Cancel placing the anchor"
      >
        Cancel
      </button>
    </div>
  );
}

/** Shown while place-note mode is waiting for a tap to drop the new note. */
function NoteArmHint() {
  const noteArmed = useShellStore((s) => s.noteArmed);
  const cancelPlaceNote = useShellStore((s) => s.cancelPlaceNote);
  if (!noteArmed) return null;
  return (
    <div className="acp-hint">
      Tap the chart where the note should go.
      <button
        type="button"
        className="acp-hint-cancel"
        onClick={cancelPlaceNote}
        aria-label="Cancel placing note"
      >
        Cancel
      </button>
    </div>
  );
}

/**
 * The build-mode HUD. Shown while we're manually building a route: a clear
 * floating card that (a) says plainly we're in build mode, (b) reads the running
 * total distance and time-to-go for the route so far, updating as each waypoint
 * lands, and (c) carries the Finish-editing button so ending the build is a
 * deliberate on-chart tap rather than a trip back into the sheet.
 *
 * It sits bottom-left, clear of the radial ring (which opens under the thumb
 * wherever we long-press) and the right-hand controls. Only up while we're
 * actively dropping waypoints, and gone the moment we finish or cancel.
 *
 * TTG reuses the same expected-speed timing the Live data fields and the editor
 * use (routeTimedSummary → summariseRouteTimed), so all three agree. With no
 * usable speed it reads "Set a speed" instead of a NaN.
 */
function RouteBuildingBanner() {
  const buildingRouteId = useRoutingStore((s) => s.buildingRouteId);
  const routes = useRoutingStore((s) => s.routes);
  const finishRoute = useRoutingStore((s) => s.finishRoute);
  const removeRoute = useRoutingStore((s) => s.removeRoute);
  const setActiveRoute = useRoutingStore((s) => s.setActiveRoute);
  const activeSheet = useShellStore((s) => s.activeSheet);
  const openSheet = useShellStore((s) => s.openSheet);
  const showToast = useShellStore((s) => s.showToast);
  const planSpeedRef = useSettingsStore((s) => s.planSpeedRef);
  const cruiseSpeedKn = useSettingsStore((s) => s.cruiseSpeedKn);
  const sog = useChartPlotterStore((s) => s.ownship.sog);

  // Derive the building route and its running summary from the stable slices,
  // rather than selecting a fresh object out of the store (which would break
  // zustand's reference equality and loop the render). The summary carries the
  // running total distance and the timed TTG on the skipper's speed basis.
  const buildingRoute = useMemo(
    () => selectBuildingRoute({ buildingRouteId, routes }),
    [buildingRouteId, routes],
  );
  const summary = useMemo(
    () =>
      buildingRoute
        ? routeTimedSummary(buildingRoute, { planSpeedRef, cruiseSpeedKn, sog })
        : null,
    [buildingRoute, planSpeedRef, cruiseSpeedKn, sog],
  );

  // The build HUD belongs up whenever a route is being built, whatever the shell
  // mode. Auto-route and the destination "Manual" build set buildingRouteId while
  // we're still in Live, and the skipper still needs the Finish button, so we no
  // longer gate on Plotting. We do stand down for an open sheet, so the HUD doesn't
  // fight a sheet that's covering the chart. All hooks above run every render; the
  // early return stays below them.
  if (!buildingRouteId || activeSheet) return null;

  const count = buildingRoute?.waypoints.length ?? 0;
  const canFinish = count >= 2;
  const totalNm = summary?.totalNm ?? 0;
  const ttg = summary ? formatTtg(summary.totalHours) : null;
  const ttgBasis = summary?.basis === 'stw-tide' ? ' (tide)' : '';

  function finish() {
    // A route needs two waypoints to make a leg, so the button's disabled until
    // then; this guard just keeps a stray call safe.
    if (!canFinish) return;
    // Make sure the just-built route is the selected one, so the editor opens on
    // it, then leave build mode and open the full leg-by-leg editor.
    setActiveRoute(buildingRouteId!);
    finishRoute();
    openSheet('route');
    showToast('Route finished — edit its legs, name and colour');
  }
  function cancel() {
    // Discard the draft cleanly: the whole half-built route goes, no orphan left
    // on the chart. removeRoute also clears any state pointing at it.
    removeRoute(buildingRouteId!);
    showToast('Route building cancelled');
  }

  return (
    <div className="acp-route-build" role="status" aria-label="Building route">
      <div className="rb-hint">
        <b>● Building route</b>
        <span>
          {count === 0
            ? 'Long-press the chart to drop the first waypoint.'
            : `${count} ${count === 1 ? 'waypoint' : 'waypoints'} · long-press to add more.`}
        </span>
      </div>
      <div className="rb-readout">
        <div className="rb-stat">
          <span className="rb-k">Distance</span>
          <span className="rb-v">{formatDistance(totalNm)}</span>
        </div>
        <div className="rb-stat">
          <span className="rb-k">TTG{ttgBasis}</span>
          <span className="rb-v">{ttg ?? 'Set a speed'}</span>
        </div>
      </div>
      <div className="rb-actions">
        <button type="button" className="rb-cancel" onClick={cancel}>
          Cancel
        </button>
        <button
          type="button"
          className="rb-finish"
          onClick={finish}
          disabled={!canFinish}
          title={canFinish ? 'Finish and edit the route' : 'Drop at least two waypoints to finish'}
        >
          Finish editing
        </button>
      </div>
    </div>
  );
}

/**
 * The follow HUD. Shown while following a route or going to a mark, over the
 * chart, in the bottom slot the CSS already keeps clear of the top data stack and
 * the right-hand controls (so it doesn't collide with the readouts, the radial
 * ring, or the build HUD). It reads live off own
 * ship: the waypoint we're making for, the range and bearing to it, and the
 * cross-track error with which way to steer back onto the leg. No fix yet shows
 * "waiting for position" rather than a row of NaNs.
 */
function ActiveNavBanner() {
  const followRouteId = useRoutingStore((s) => s.followRouteId);
  const goto = useRoutingStore((s) => s.goto);
  const routes = useRoutingStore((s) => s.routes);
  const marks = useRoutingStore((s) => s.marks);
  const activeLegIndex = useRoutingStore((s) => s.activeLegIndex);
  const followFromLegIndex = useRoutingStore((s) => s.followFromLegIndex);
  const stopFollowing = useRoutingStore((s) => s.stopFollowing);
  const stopGoto = useRoutingStore((s) => s.stopGoto);
  const stepActiveLeg = useRoutingStore((s) => s.stepActiveLeg);
  const activeSheet = useShellStore((s) => s.activeSheet);
  const showToast = useShellStore((s) => s.showToast);
  const position = useChartPlotterStore((s) => s.ownship.position);
  const sog = useChartPlotterStore((s) => s.ownship.sog);
  const cruiseSpeedKn = useSettingsStore((s) => s.cruiseSpeedKn);
  // Collapsed by default: at rest this is a small pill with the waypoint and the
  // range on it, and it opens to the full readout and the leg controls on a tap.
  // Expanded it was eating the bottom third of a phone screen, which is chart we
  // could be looking at.
  const [expanded, setExpanded] = useState(false);
  if (activeSheet || (!followRouteId && !goto)) return null;

  const stop = () => {
    if (followRouteId) stopFollowing();
    else stopGoto();
    showToast('Stopped');
  };

  const title = followRouteId ? 'Following route' : 'Going to waypoint';

  // The name of the point we're making for: the active leg's end waypoint on a
  // followed route, or the mark on a goto.
  const followRoute = followRouteId ? routes.find((r) => r.id === followRouteId) : undefined;
  const nLegs = followRoute ? legCount(followRoute) : 0;
  const curLeg = followRoute ? clampFollowLegIndex(followRoute, activeLegIndex ?? 0) : 0;
  // On the approach leg we're making for WP1 itself; on a real leg it's the
  // waypoint at the far end of it.
  const onApproach = curLeg === APPROACH_LEG_INDEX;
  let targetName = '';
  if (followRoute && followRoute.waypoints.length >= 2) {
    const tw = onApproach ? followRoute.waypoints[0] : followRoute.waypoints[curLeg + 1];
    targetName = tw?.name?.trim() || `WP${onApproach ? 1 : curLeg + 2}`;
  } else if (goto) {
    targetName = marks.find((m) => m.id === goto.markId)?.name || 'mark';
  }

  // Previous / Next waypoint stepping, only when following a route with more than
  // one leg to move between (a goto has nothing to step). The store clamps, so we
  // also disable the buttons at the ends for a clear "you're at the start/finish".
  const step = (delta: number) => {
    haptic('tick');
    stepActiveLeg(delta);
  };
  const legControls =
    followRouteId && nLegs > 1 ? (
      <div className="pb-legstep" role="group" aria-label="Active waypoint">
        <button
          type="button"
          className="pb-step"
          onClick={() => step(-1)}
          disabled={curLeg <= APPROACH_LEG_INDEX}
          title="Previous waypoint"
          aria-label="Previous waypoint"
        >
          ‹ Prev
        </button>
        <span className="pb-legnum">
          {onApproach ? 'To start' : `Leg ${curLeg + 1}/${nLegs}`}
        </span>
        <button
          type="button"
          className="pb-step"
          onClick={() => step(1)}
          disabled={curLeg >= nLegs - 1}
          title="Next waypoint"
          aria-label="Next waypoint"
        >
          Next ›
        </button>
      </div>
    ) : null;

  // No fix yet: don't show numbers we don't have. The Prev/Next stepping still
  // works, so a skipper can set the starting waypoint before moving off.
  const toggle = (body: string) => (
    <button
      type="button"
      className="pb-txt pb-toggle"
      onClick={() => setExpanded((v) => !v)}
      aria-expanded={expanded}
      title={expanded ? 'Show less' : 'Show the full readout'}
    >
      <b>{title}{targetName ? ` · ${targetName}` : ''}</b>
      <span>{body}</span>
    </button>
  );

  if (!position) {
    return (
      <div className={`acp-plot-banner${expanded ? ' pb-expanded' : ' pb-collapsed'}`}>
        {toggle('Waiting for position…')}
        <div className="pb-controls">
          {expanded ? legControls : null}
          <button type="button" className="pb-done" onClick={stop}>
            Stop
          </button>
        </div>
      </div>
    );
  }

  const leg = selectActiveLeg({ followRouteId, activeLegIndex, followFromLegIndex, goto, routes, marks }, position);
  // Range is the direct distance to the waypoint; bearing is straight to it. The
  // XTE arrow says which way to steer: + xte is right of track, so steer to port.
  const rangeNm = leg ? rhumbDistanceNm(position, leg.target) : null;
  const side = leg ? steerSide(leg.xteNm) : 'on';
  const steerText =
    side === 'left' ? '◀ steer to port' : side === 'right' ? 'steer to starboard ▶' : 'on track';
  // TTG to the waypoint and to the route's end (fb-msn01ago-1). On live SOG
  // while we are actually moving, else the planning speed, so a boat waiting
  // on a mooring still reads a useful number rather than infinity.
  const ttgSpeedKn = sog > 0.5 ? sog : cruiseSpeedKn;
  let restNm = 0;
  if (followRoute && !onApproach) {
    for (let i = curLeg + 1; i < followRoute.waypoints.length - 1; i++) {
      restNm += rhumbDistanceNm(followRoute.waypoints[i].position, followRoute.waypoints[i + 1].position);
    }
  }
  const ttgWp =
    rangeNm != null && ttgSpeedKn > 0 ? formatTtg(rangeNm / ttgSpeedKn) : null;
  const ttgEnd =
    rangeNm != null && ttgSpeedKn > 0 && followRoute
      ? formatTtg((rangeNm + restNm) / ttgSpeedKn)
      : null;
  const ttgText = ttgWp
    ? ` · TTG ${ttgWp}${ttgEnd && ttgEnd !== ttgWp ? ` · end ${ttgEnd}` : ''}`
    : '';
  const detail = leg
    ? `RNG ${formatDistance(rangeNm ?? 0)} · BRG ${formatBearing(leg.brgDeg)} · XTD ${Math.abs(leg.xteNm).toFixed(2)} nm ${steerText}${ttgText}`
    : 'Active leg to the destination.';
  // Collapsed we keep the two numbers we steer by, range and bearing, and leave
  // the cross-track and the leg controls for the tap.
  const brief = leg
    ? `RNG ${formatDistance(rangeNm ?? 0)} · BRG ${formatBearing(leg.brgDeg)}`
    : 'Active leg to the destination.';

  return (
    // Expanded, the readout takes a full-width row of its own and the controls
    // sit on a second row beneath it. Side by side, the numbers were squeezed
    // into a narrow column that wrapped over five or six lines while the
    // buttons stacked up tall beside them (fb-msbvic9h-3, third report of it).
    <div className={`acp-plot-banner${expanded ? ' pb-expanded' : ' pb-collapsed'}`}>
      {toggle(expanded ? detail : brief)}
      <div className="pb-controls">
        {expanded ? legControls : null}
        <button type="button" className="pb-done" onClick={stop}>
          Stop
        </button>
      </div>
    </div>
  );
}

/** One live alarm: a stable identity for acknowledging, and the words shown. */
interface AlarmEntry {
  key: string;
  text: string;
}

/**
 * The live safety alarms with their identities. The identity is what a tap on
 * the banner acknowledges (fb-msn7sgm6-0), so it must be stable while the
 * condition holds: the TEXT of the off-track alarm changes every fix as the
 * number moves, and acknowledging by text would un-acknowledge itself a second
 * later.
 */
function useAlarmEntries(): AlarmEntry[] {
  const position = useChartPlotterStore((s) => s.ownship.position);
  const routes = useRoutingStore((s) => s.routes);
  const marks = useRoutingStore((s) => s.marks);
  const followRouteId = useRoutingStore((s) => s.followRouteId);
  const activeRouteId = useRoutingStore((s) => s.activeRouteId);
  const activeLegIndex = useRoutingStore((s) => s.activeLegIndex);
  const followFromLegIndex = useRoutingStore((s) => s.followFromLegIndex);
  const alarmsLive = useAlarmsStore((s) => s.live);
  const alarms: AlarmEntry[] = [];
  if (!position) return alarms;
  // GNSS integrity alarms, from the live snapshot the GnssAlarmsRunner fills on
  // the device feed (spoofing jump and lost fix lead; poor accuracy is the softer
  // one). Driven off the live state so the banner, siren and haptics all agree.
  if (alarmsLive.gnssJump) {
    alarms.push({ key: 'gnssJump', text: 'Possible GNSS spoofing: position jumped' });
  }
  if (alarmsLive.gnssLost) {
    const age = alarmsLive.gnssLost.ageSec;
    alarms.push({ key: 'gnssLost', text: `GNSS fix lost${age != null ? ` (${age}s ago)` : ''}` });
  }
  if (alarmsLive.gnssAccuracy) {
    const g = alarmsLive.gnssAccuracy;
    alarms.push({ key: 'gnssAccuracy', text: `GNSS accuracy poor: ±${Math.round(g.accuracyM)} m (limit ${Math.round(g.limitM)} m)` });
  }
  for (const m of marks) {
    if (typeof m.arrivalRadiusNm === 'number' && rhumbDistanceNm(position, m.position) <= m.arrivalRadiusNm) {
      alarms.push({ key: `at:${m.id}`, text: `At ${m.name || 'mark'}` });
    }
  }
  for (const r of routes) {
    // Only the followed or selected route: arrivals on other stored routes in the
    // same water aren't ones we're navigating to, so they don't banner.
    if (r.id !== followRouteId && r.id !== activeRouteId) continue;
    for (const w of r.waypoints) {
      const rad = effectiveWaypointRadius(r, w);
      if (rad != null && rhumbDistanceNm(position, w.position) <= rad) alarms.push({ key: `at:${w.id}`, text: `At ${w.name || 'WP'}` });
    }
  }
  if (followRouteId) {
    const route = routes.find((r) => r.id === followRouteId);
    // Nothing to be off on the approach to WP1: the track is drawn from wherever we
    // are to the mark and redrawn on every fix, so there's no planned line to wander
    // from and no off-track call to make, by either route below.
    if (route && route.waypoints.length >= 2 && activeLegIndex !== APPROACH_LEG_INDEX) {
      // Measure the off-track against the leg we're steering. Prefer the stored
      // active leg (activated follow) so the banner matches the siren and HUD;
      // fall back to the nearest-leg pick for a legacy follow.
      if (activeLegIndex != null) {
        const f = followLeg(route, position, clampLegIndex(route, activeLegIndex));
        if (f) {
          const xtd = effectiveLegXtd(route, f.fromId);
          if (xtd != null && Math.abs(f.xteNm) > xtd) {
            alarms.push({ key: 'offTrack', text: `Off track ${Math.abs(f.xteNm).toFixed(2)} nm (XTD ${xtd.toFixed(2)})` });
          }
        }
      } else {
        const mon = monitorRoute(route, position, followFromLegIndex ?? 0);
        if (mon.activeLegIndex != null && mon.active) {
          const fromId = route.waypoints[mon.activeLegIndex].id;
          const xtd = effectiveLegXtd(route, fromId);
          if (xtd != null && Math.abs(mon.active.xteNm) > xtd) {
            alarms.push({ key: 'offTrack', text: `Off track ${Math.abs(mon.active.xteNm).toFixed(2)} nm (XTD ${xtd.toFixed(2)})` });
          }
        }
      }
    }
  }
  // Configured underway alarms (charted depth, charted-hazard proximity),
  // computed in MapView from the live position and the chart cells.
  if (alarmsLive.depth) {
    alarms.push({
      key: 'depth',
      text: (() => {
        const d = alarmsLive.depth!;
        const tide = d.tideM == null ? 'no tide' : `tide ${d.tideM >= 0 ? '+' : ''}${d.tideM.toFixed(1)}`;
        return `Shallow water: ${d.underKeelM.toFixed(1)} m under keel (charted ${d.chartedM.toFixed(1)} ${tide} − draught ${d.draughtM.toFixed(1)}; limit ${d.limitM.toFixed(1)} m)`;
      })(),
    });
  }
  if (alarmsLive.proximity) {
    alarms.push({ key: 'proximity', text: `${alarmsLive.proximity.label} ${alarmsLive.proximity.rangeNm.toFixed(2)} nm off` });
  }
  if (alarmsLive.clearing) {
    for (const c of alarmsLive.clearing) alarms.push({ key: `clearing:${c.label}`, text: `Clearing: ${c.label} — unsafe side` });
  }
  if (alarmsLive.clearingDepth) {
    for (const c of alarmsLive.clearingDepth)
      alarms.push({ key: `clearingDepth:${c.label}`, text: `Clearing depth: ${c.label} — charted ${c.chartedM.toFixed(1)} m under boat` });
  }
  if (alarmsLive.ais?.proximity) {
    alarms.push({ key: 'aisProximity', text: `AIS: ${alarmsLive.ais.proximity.label} ${alarmsLive.ais.proximity.rangeNm.toFixed(2)} nm off` });
  }
  if (alarmsLive.ais?.cpa) {
    const c = alarmsLive.ais.cpa;
    alarms.push({ key: 'aisCpa', text: `AIS CPA: ${c.label} — ${c.cpaNm.toFixed(2)} nm in ${c.tcpaMin.toFixed(0)} min` });
  }
  if (alarmsLive.anchor) {
    const a = alarmsLive.anchor;
    alarms.push({
      key: 'anchor',
      text:
        a.mode === 'zone'
          ? `Anchor dragging: ${Math.round(a.distanceNm * 1852)} m out, outside the anchor zone`
          : `Anchor dragging: ${Math.round(a.distanceNm * 1852)} m out (swing ${Math.round(a.radiusNm * 1852)} m)`,
    });
  }
  if (alarmsLive.grounding) {
    const g = alarmsLive.grounding;
    alarms.push({ key: 'grounding', text: `Grounding ahead: shallow water in ${g.etaMin.toFixed(0)} min (${g.distanceNm.toFixed(2)} nm)` });
  }
  if (alarmsLive.tideHeight) {
    const th = alarmsLive.tideHeight;
    alarms.push({ key: 'tideHeight', text: `Tide height ${th.heightM.toFixed(1)} m — ${th.mode} ${th.limitM.toFixed(1)} m limit` });
  }
  if (alarmsLive.lowBattery) {
    alarms.push({ key: 'lowBattery', text: `Low battery: ${alarmsLive.lowBattery.levelPct}%` });
  }
  return alarms;
}

/**
 * The acknowledge identities for a set of entries: the entry's own key plus its
 * family key, so acknowledging the banner also quietens the siren, whose gate
 * thinks in the plain AlarmKey names.
 */
function ackKeysFor(entries: AlarmEntry[]): string[] {
  const keys = new Set<string>();
  for (const e of entries) {
    keys.add(e.key);
    const base = e.key.split(':')[0];
    keys.add(base === 'at' ? 'waypointArrival' : base);
  }
  return [...keys];
}

/** The live safety-alarm strings, with acknowledged episodes filtered out. */
function useAlarmStrings(): string[] {
  const entries = useAlarmEntries();
  const acked = useAlarmsStore((s) => s.acked);
  return entries.filter((e) => !acked[e.key]).map((e) => e.text);
}

/**
 * The safety-alarm strip. Rendered in the always-on-top safety layer (above the
 * night dim veil), so a dimmed screen can never hide a live alarm — the dim
 * knocks back the chart, not the warnings. Carries its own strong red rather than
 * the chart palette so it reads identically day or night.
 */
function SafetyAlarmBanner() {
  const entries = useAlarmEntries();
  const acked = useAlarmsStore((s) => s.acked);
  const acknowledgeAlarms = useAlarmsStore((s) => s.acknowledgeAlarms);
  const pruneAcked = useAlarmsStore((s) => s.pruneAcked);
  const masterMuted = useAlarmsStore((s) => s.masterMuted);
  // Episode bookkeeping: when a condition clears, its acknowledgement goes with
  // it, so the NEXT time it fires it banners and sounds afresh. Keyed on the
  // identity list, which only changes when an alarm starts or stops.
  const keysNow = ackKeysFor(entries).join('|');
  useEffect(() => {
    pruneAcked(keysNow ? keysNow.split('|') : []);
  }, [keysNow, pruneAcked]);
  const unacked = entries.filter((e) => !acked[e.key]);
  if (!unacked.length) return null;
  // When muted the alarm still banners (detection is untouched), but we lead with
  // a mute glyph so it's obvious the siren is off on purpose, not a dead speaker.
  return (
    <button
      type="button"
      className="acp-alarm"
      role="alert"
      // The shell layer is pointer-events:none so the chart pans through the
      // HUD; a child that wants taps must opt back in, and this one forgot,
      // so tapping the alarm tapped the chart underneath (fb-msndfgnf-3).
      // Same lesson as fb-msc9xbe8-0, re-learned once more.
      style={{ pointerEvents: 'auto', cursor: 'pointer' }}
      onClick={() => {
        // Acknowledge THIS episode (fb-msn7sgm6-0): banner and siren go quiet,
        // detection carries on untouched, and a fresh episode re-alarms.
        haptic('tick');
        acknowledgeAlarms(ackKeysFor(unacked));
      }}
      aria-label="Acknowledge these alarms — detection stays on"
    >
      {masterMuted ? '🔇 ' : '⚠ '}
      {unacked.map((e) => e.text).join('  ·  ')}
      {masterMuted ? '  ·  (muted)' : ''}
      <span style={{ display: 'block', marginTop: 2, opacity: 0.85, fontSize: 11 }}>tap to acknowledge</span>
    </button>
  );
}

/**
 * One status strip, not a stack. Shows the single most relevant non-alarm banner
 * by priority: active navigation, then route building, then an armed plotting
 * tool. A live safety alarm takes the screen instead (SafetyAlarmBanner, above
 * the dim veil), so this stands down while one is up to keep to a single strip.
 */
function StatusBanner() {
  const alarms = useAlarmStrings();
  const followRouteId = useRoutingStore((s) => s.followRouteId);
  const goto = useRoutingStore((s) => s.goto);
  const buildingRouteId = useRoutingStore((s) => s.buildingRouteId);
  const activeSheet = useShellStore((s) => s.activeSheet);
  if (alarms.length) return null;
  // Building outranks following. The build strip carries the only exit from
  // build mode, and with a go-to running it never showed at all, which left a
  // route with no way to finish it (fb-msmb4e4d-8: "there's no finished
  // building button"). The follow banner is ambient status and comes straight
  // back the moment the build is done.
  if (buildingRouteId && !activeSheet) return <RouteBuildingBanner />;
  if (!activeSheet && (followRouteId || goto)) return <ActiveNavBanner />;
  return <PlotBanner />;
}

/**
 * Quick screen-dim control on the chart HUD. A small brightness tab at the left
 * edge that expands a slider, for knocking the whole screen back on a really dark
 * night without diving into the menu. It lives in the safety layer above the dim
 * veil, so it stays readable and usable even at the darkest setting (the menu's
 * own copy of this slider dims with everything else, which is exactly why a
 * quick, always-bright control earns its place). Shown only on the night palette,
 * where the dim has any effect; the menu slider covers the rest.
 */
function ScreenDimControl() {
  const palette = useChartPlotterStore((s) => s.palette);
  const nightBrightness = useSettingsStore((s) => s.nightBrightness);
  const setSettings = useSettingsStore((s) => s.set);
  const [open, setOpen] = useState(false);
  if (palette !== 'night') return null;
  const pct = Math.round(nightBrightness * 100);
  return (
    <div className={`acp-dim-ctrl${open ? ' open' : ''}`}>
      <button
        type="button"
        className="acp-dim-fab"
        aria-label={`Screen brightness ${pct} percent — tap to adjust`}
        aria-expanded={open}
        onClick={() => {
          haptic('tick');
          setOpen((o) => !o);
        }}
      >
        <span aria-hidden="true">◐</span>
        <span className="acp-dim-pct">{pct}%</span>
      </button>
      {open && (
        <div className="acp-dim-pop" role="group" aria-label="Screen brightness">
          <input
            className="acp-range"
            type="range"
            min={0.2}
            max={1}
            step={0.05}
            value={nightBrightness}
            aria-label="Screen brightness"
            onChange={(e) => setSettings({ nightBrightness: Number(e.target.value) })}
          />
        </div>
      )}
    </div>
  );
}

/** A confirm prompt: a question with an action, e.g. offering to switch to
 * Plotting to build a route. Dismissible; the action runs on confirm. */
function ConfirmPrompt() {
  const confirm = useShellStore((s) => s.confirm);
  const clearConfirm = useShellStore((s) => s.clearConfirm);
  if (!confirm) return null;
  return (
    <div className="acp-confirm-wrap" role="dialog" aria-modal="true" onClick={() => { haptic('select'); clearConfirm(); }}>
      <div className="acp-confirm" onClick={(e) => e.stopPropagation()}>
        <p>{confirm.message}</p>
        <div className="acp-confirm-row">
          <button type="button" className="acp-btn" onClick={() => { haptic('select'); clearConfirm(); }}>
            Not now
          </button>
          <button
            type="button"
            className="acp-btn"
            onClick={() => {
              // This is the affirmative on a consequential prompt (delete a route,
              // cancel a live MOB, and the like), so it earns the firmer commit
              // buzz — one central place covers every showConfirm caller.
              haptic('commit');
              const fn = confirm.onConfirm;
              clearConfirm();
              fn();
            }}
          >
            {confirm.confirmLabel}
          </button>
          {confirm.altLabel && confirm.onAlt && (
            <button
              type="button"
              className="acp-btn primary"
              onClick={() => {
                haptic('commit');
                const fn = confirm.onAlt!;
                clearConfirm();
                fn();
              }}
            >
              {confirm.altLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export function Shell() {
  // Startup: come back where the skipper left us. The position source is now
  // remembered on its own (like the chart provider, tide source and last map
  // view), so a cold start comes back on whichever source the skipper left us on
  // rather than forcing the simulator on someone who was using the device
  // receiver. When that source is the device GNSS we engage the tracking lock so
  // the camera centres on the live fix as soon as it lands, rather than sitting on
  // a hardcoded default.
  useEffect(() => {
    if (usePositionSourceStore.getState().sourceKind === 'device') {
      useShellStore.getState().setTracking(true);
    }
  }, []);
  return (
    <div style={{ position: 'fixed', inset: 0 }}>
      <MapView instanceId="main" />
      {/* Night dim veil: a click-through full-screen layer that dims/warm-shifts
          everything (chart, satellite, any bright panel) at night, driven by the
          brightness slider. Invisible on day/dusk (its alpha var is 0). */}
      <div className="acp-night-dim" aria-hidden="true" />
      {/* The black box. First in the list because a freeze it cannot see is a
          freeze we get to argue about instead of read. */}
      <AppStateRunner />
      <HeartbeatRunner />
      <SimRunner />
      <BsbManifestLoader />
      <ChartPacksLoader />
      <UpdateCheckLoader />
      <FeedbackSyncRunner />
      <ChartEdgeRunner />
      <NoaaManifestLoader />
      <FesStationsLoader />
      <AisSimRunner />
      <AisInternetRunner />
      <HapticsRunner />
      <AlarmSoundRunner />
      <GnssAlarmsRunner />
      <BatteryAlarmRunner />
      <GlassRunner />
      <NightRunner />
      <AutoPaletteRunner />
      <AutoVariationRunner />
      <KeepAwakeRunner />
      <AndroidBackRunner />
      <GnssHealthRunner />
      <RouteFollowRunner />

      <div className="acp-shell">
        <Chrome />
        <FixArmHint />
        <NoteArmHint />
        <AnchorArmHint />
        <AreaSelectHint />
        <AreaDownloadCard />
        <StatusBanner />
        <TrainingBanner />
        <BackgroundServiceBanner />
        <ArmedStrip />
        <RebuildControl />
        <div className="acp-chip-stack">
          <GnssFixAgeChip />
          <WeatherOverlayBar />
          <TideScrubber />
          <CatzocLegendChip />
          <PlanVisibilityChip />
          <EditPlanChip />
          <TideHeightChip />
          <CommunityCountChip />
        </div>

        <Backdrop />
        <ModeSheet />
        <DataFieldSheet />
        <VesselSheet />
        <ChartTapSheet />
        <DestinationSheet />
        <WhatsHereSheet />
        <NoteSheet />
        <FeatureSheet />
        <RouteSheet />
        <PlanningSheet />
        <ConditionsSheet />
        <WeatherTableSheet />
        <TidesSheet />
        <AisSheet />
        <ForecastSheet />
        <FixBuilderSheet />
        <RunningFixSheet />
        <DerivedSheet />
        <CtsSheet />
        <TracksSheet />
        <WaypointSheet />
        <RouteAlarmsSheet />
        <LegXtdSheet />
        <AlarmsSheet />
        <ClearingSheet />
        <PilotageSheet />
        <TransitSheet />
        <HeadingSheet />
        <ClearingDepthSheet />
        <MenuSheet />
        <MobSearchSheet />
        <DiamondSheet />
        <EblVrmSheet />
        <SearchSheet />
        <PilotagePlansSheet />
        <PilotageCommunitySheet />
        <RadialRing />
        <TrackActionsPopover />
        <MeasureReadout />
        <ConfirmPrompt />
        <Toast />
        <UpdatePrompt />
        <PilotagePlanToolbar />
        <FeedbackButton />
      </div>

      {/* Safety layer — painted above the night dim veil, so a dimmed screen can
          never swallow the man-overboard readout or a live alarm (the dim knocks
          back the chart, not the warnings), and the quick brightness control
          stays reachable even at the darkest setting. */}
      <div className="acp-safety-top">
        <MobReadout />
        <SafetyAlarmBanner />
        <ScreenDimControl />
      </div>

      <FirstBootOverlay />
      {/* First-run coach-mark tour. Sits above the shell but below the safety
          layer, and hides itself while any alarm or MOB is live. */}
      <Walkthrough />
    </div>
  );
}
